import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { log } from "@/lib/logger";
import { toast } from "sonner";
import { Heart, MapPin, Mail, Phone, Globe, Send, Building2, Sparkles, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";

export function ApplyDialog({ open, onOpenChange, company, youngMode: initialYoung = false }) {
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [message, setMessage] = useState("");
  const [position, setPosition] = useState("");
  const [young, setYoung] = useState(initialYoung);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const send = async () => {
    if (!user) { onOpenChange(false); nav("/login"); return; }
    if (!message.trim()) { toast.error(t(lang, "fill_all")); return; }
    setLoading(true);
    try {
      await http.post("/applications", { company_id: company.id, message, position_wanted: position || null });
      toast.success(t(lang, "application_sent"));
      setMessage(""); setPosition("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  const generateAI = async () => {
    if (!user) { nav("/login"); return; }
    if (!young && !user.diploma) { toast.error(t(lang, "fill_diploma_in_profile")); return; }
    setAiLoading(true);
    try {
      const { data } = await http.post("/ai/cover-letter", {
        company_id: company.id,
        position_wanted: position || null,
        young_no_experience: young,
      });
      setMessage(data.letter);
      toast.success(t(lang, "ai_letter_ready"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
    } finally { setAiLoading(false); }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="apply-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t(lang, "apply_title")}</DialogTitle>
          <p className="text-sm text-slate-500">{company.name} · {company.city}</p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t(lang, "apply_position")}</Label>
            <Input data-testid="apply-position" value={position} onChange={(e) => setPosition(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>{t(lang, "apply_message")}</Label>
              <Button
                data-testid="ai-cover-letter-btn"
                type="button" size="sm" variant="outline"
                disabled={aiLoading}
                onClick={generateAI}
                className="rounded-full h-8 text-xs border-[#F97316] text-[#C2410C] hover:bg-[#FFF7ED]"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" /> {aiLoading ? t(lang, "ai_generating") : t(lang, "ai_cover_letter")}
              </Button>
            </div>
            <div className="mb-2 flex items-start gap-2 rounded-lg bg-[#FFF7ED] border border-[#F97316]/30 p-2.5">
              <Checkbox id="young-check" data-testid="young-checkbox" checked={young} onCheckedChange={setYoung} className="mt-0.5" />
              <div>
                <label htmlFor="young-check" className="text-xs font-semibold text-[#C2410C] cursor-pointer">{t(lang, "young_checkbox")}</label>
                <p className="text-[11px] text-slate-600 leading-snug mt-0.5">{t(lang, "young_hint")}</p>
              </div>
            </div>
            <Textarea data-testid="apply-message" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t(lang, "cancel")}</Button>
          <Button data-testid="apply-send-btn" disabled={loading} onClick={send} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">
            <Send className="w-4 h-4 mr-2" /> {loading ? "..." : t(lang, "send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FavButton({ companyId }) {
  const { user, lang } = useAuth();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) { toast.info(t(lang, "cta_login")); return; }
    setBusy(true);
    try {
      await http.post(`/favorites/${companyId}`);
      toast.success(lang === "fr" ? "Ajouté aux favoris" : "Added to favorites");
    } catch (e) {
      // Duplicate favorite (409) is common when the user clicks twice; only
      // surface real errors and always release the button.
      if (e?.response?.status !== 409) {
        toast.error(lang === "fr" ? "Ajout impossible" : "Cannot add favorite");
        log.warn("[favorites] toggle failed:", e?.message || e);
      }
    } finally { setBusy(false); }
  };
  return (
    <Button data-testid={`fav-btn-${companyId}`} onClick={toggle} disabled={busy} variant="outline" size="sm" className="rounded-full">
      <Heart className="w-4 h-4 mr-1.5" /> {t(lang, "add_favorite")}
    </Button>
  );
}

export function CompanySheet({ company, onClose, onApply }) {
  const { lang } = useAuth();
  if (!company) return null;
  return (
    <Sheet open={!!company} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="company-sheet" side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1E3A8A]" />
            </div>
            <div>
              <SheetTitle className="font-display text-2xl text-left">{company.name}</SheetTitle>
              <p className="text-sm text-slate-500">{company.sector} · {company.size || "—"}</p>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {company.match_score && (
            <div className="rounded-xl bg-[#FFF7ED] border border-[#F97316]/30 p-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#C2410C]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#C2410C] font-semibold">AI Match</span>
              </div>
              <p className="mt-2 text-3xl font-display font-bold text-[#C2410C]">{company.match_score}%</p>
            </div>
          )}

          <p className="text-sm text-slate-700 leading-relaxed">{company.description}</p>

          <div className="flex flex-wrap gap-1.5">
            {company.hosts_interns && (
              <Badge className="bg-[#1E3A8A] text-white rounded-full">{t(lang, "hosts_interns_yes")}</Badge>
            )}
            {(company.contract_types || []).map((ct) => (
              <Badge key={ct} variant="secondary" className="rounded-full">{t(lang, `contract_${ct}`)}</Badge>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="flex gap-2 items-start text-sm">
              <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <span>{company.address}, {company.city}, {company.country}</span>
            </div>
            {company.email && <div className="flex gap-2 items-center text-sm"><Mail className="w-4 h-4 text-slate-400" /><a href={`mailto:${company.email}`} className="hover:underline">{company.email}</a></div>}
            {company.phone && <div className="flex gap-2 items-center text-sm"><Phone className="w-4 h-4 text-slate-400" />{company.phone}</div>}
            {company.website && <div className="flex gap-2 items-center text-sm"><Globe className="w-4 h-4 text-slate-400" /><a href={company.website} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:underline">{company.website}</a></div>}
          </div>

          {company.typical_needs?.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "typical_needs")}</p>
              <div className="flex flex-wrap gap-2">
                {company.typical_needs.map((n) => <Badge key={n} variant="secondary" className="rounded-full">{n}</Badge>)}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
            <Button data-testid={`apply-open-${company.id}`} onClick={onApply} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">
              <Send className="w-4 h-4 mr-1.5" /> {t(lang, "apply_spontaneous")}
            </Button>
            <FavButton companyId={company.id} />
            <Link to={`/vitrine/${company.id}`} target="_blank">
              <Button variant="outline" size="sm" className="rounded-full" data-testid={`vitrine-link-${company.id}`}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {t(lang, "vitrine_public")}
              </Button>
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
